import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Flame, Check, Lock, Sparkles, BookOpen, Trophy, RefreshCw, ChevronRight, Volume2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import PetLangChat from './PetLangChat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'history', label: 'Historia', emoji: '📜', accent: 'from-amber-300 to-orange-400' },
  { id: 'trivia',  label: 'Trivia',   emoji: '🎲', accent: 'from-cyan-300 to-sky-400' },
  { id: 'language',label: 'Språk',    emoji: '🌍', accent: 'from-emerald-300 to-teal-400' },
];

const ERA_COLOR = {
  Ancient: '#FDF0D5', Medieval: '#CDB4DB', Renaissance: '#FFC8DD', Modern: '#A2D2FF',
  Trivia: '#BAE6FD', Language: '#BBF7D0', default: '#B9FBC0',
};

function pickColor(category, era = '') {
  if (category === 'language') return ERA_COLOR.Language;
  if (category === 'trivia') return ERA_COLOR.Trivia;
  const e = era.toLowerCase();
  if (e.includes('ancient') || e.includes('egypt') || e.includes('rome') || e.includes('greece')) return ERA_COLOR.Ancient;
  if (e.includes('medieval') || e.includes('middle')) return ERA_COLOR.Medieval;
  if (e.includes('renaiss')) return ERA_COLOR.Renaissance;
  if (e.includes('modern') || e.includes('industrial') || e.includes('20th') || e.includes('19th')) return ERA_COLOR.Modern;
  return ERA_COLOR.default;
}

function speak(text, langCode) {
  if (!window.speechSynthesis || !text) return;
  const u = new SpeechSynthesisUtterance(text);
  const map = { en: 'en-US', es: 'es-ES', fr: 'fr-FR', it: 'it-IT', de: 'de-DE', ja: 'ja-JP' };
  u.lang = map[langCode] || 'sv-SE';
  u.rate = 0.9;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
}

function PathNode({ node, index, onOpen, category }) {
  const isToday = node.is_today;
  const isCompleted = node.completed;
  const isAvailable = node.available;
  const locked = !isAvailable && !isToday;
  const side = index % 4;
  const marginClass = side === 0 ? 'ml-0' : side === 1 ? 'ml-16' : side === 2 ? 'ml-32' : 'ml-16';
  const color = pickColor(category, node.era || '');
  const canClick = isAvailable || isToday;

  return (
    <button
      onClick={() => canClick && onOpen(node)}
      disabled={!canClick}
      className={`${marginClass} relative group flex items-center gap-3 transition-transform ${canClick ? 'hover:scale-105' : 'opacity-50 cursor-not-allowed'}`}
      data-testid={`learn-path-node-${node.date}`}
    >
      <div
        className={`relative w-16 h-16 rounded-full border-4 ${isCompleted ? 'border-green-500' : isToday ? 'border-pink-500 animate-pulse-soft' : 'border-gray-400'} flex items-center justify-center shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]`}
        style={{ backgroundColor: color }}
      >
        {isCompleted ? <Check className="w-8 h-8 text-green-700" strokeWidth={3} />
          : locked ? <Lock className="w-7 h-7 text-gray-600" />
          : isToday ? <Sparkles className="w-7 h-7 text-pink-600" />
          : <BookOpen className="w-7 h-7 text-gray-700" />}
        {isToday && !isCompleted && (
          <span className="absolute -top-2 -right-2 bg-pink-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black">
            IDAG
          </span>
        )}
      </div>
      <div className="text-left">
        <p className="text-xs font-semibold opacity-70">{node.date}</p>
        <p className="text-sm font-bold font-['Fredoka'] leading-tight line-clamp-2 max-w-[220px]">
          {node.topic || (locked ? 'Kommer snart' : isToday ? 'Dagens lektion' : '—')}
        </p>
        {node.era && <p className="text-xs opacity-70 italic">{node.era}</p>}
        {isCompleted && <p className="text-xs font-bold text-green-700">Poäng: {node.score}%</p>}
      </div>
    </button>
  );
}

function LessonModal({ lesson, category, language, onClose, onComplete }) {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState('fact');

  const submit = async () => {
    setSubmitting(true);
    try {
      const answerArr = lesson.questions.map((_, i) => answers[i] ?? -1);
      const { data } = await axios.post(
        `${API}/learn/complete`,
        { category, language: language || null, answers: answerArr },
        { withCredentials: true }
      );
      setResult(data);
      setStep('result');
      toast.success(`+${data.points_earned} poäng! ${data.correct_count}/${data.total} rätt 🎉`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Misslyckades skicka');
    } finally {
      setSubmitting(false);
    }
  };

  const allAnswered = lesson.questions.every((_, i) => answers[i] !== undefined);
  const isLanguage = category === 'language';
  const pronunciation = lesson.lesson?.pronunciation;
  const example = lesson.lesson?.example_sentence;

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="learn-lesson-modal">
      <div className="neo-card bg-white dark:bg-[#252545] max-w-xl w-full max-h-[85vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}>
        {step === 'fact' && (
          <>
            <p className="text-xs font-bold uppercase tracking-wider opacity-70 mb-1">{lesson.era}</p>
            <div className="flex items-start justify-between gap-2 mb-3">
              <h2 className="text-3xl font-bold font-['Fredoka']" data-testid="learn-lesson-topic">
                {lesson.topic}
              </h2>
              {isLanguage && (
                <button onClick={() => speak(lesson.topic, language)}
                  className="neo-btn p-2 bg-[#BBF7D0]" title="Spela upp"
                  data-testid="learn-speak-topic">
                  <Volume2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="rounded-2xl border-2 border-black p-4 mb-4 text-base font-semibold italic leading-relaxed"
              style={{ backgroundColor: pickColor(category, lesson.era) }}
              data-testid="learn-lesson-fact">
              {lesson.fact}
            </div>

            {isLanguage && pronunciation && (
              <div className="rounded-xl border-2 border-black p-3 mb-3 bg-[#FEF3C7]">
                <p className="text-[11px] font-bold uppercase tracking-widest opacity-60 mb-1">🗣️ Uttal</p>
                <p className="text-sm font-mono font-semibold">{pronunciation}</p>
              </div>
            )}
            {isLanguage && example && (
              <div className="rounded-xl border-2 border-black p-3 mb-4 bg-[#E0F4E1]">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-[11px] font-bold uppercase tracking-widest opacity-60 mb-1">💬 Exempel</p>
                    <p className="text-sm leading-relaxed">{example}</p>
                  </div>
                  <button onClick={() => speak(example.split(' — ')[0], language)}
                    className="neo-btn p-2 bg-white"
                    data-testid="learn-speak-example">
                    <Volume2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {lesson.lesson && (
              <div className="space-y-4 mb-5" data-testid="learn-lesson-body">
                {lesson.lesson.intro && (
                  <section>
                    <p className="text-[11px] font-bold uppercase tracking-widest opacity-60 mb-1">📖 Intro</p>
                    <p className="text-sm leading-relaxed">{lesson.lesson.intro}</p>
                  </section>
                )}
                {lesson.lesson.key_points?.length > 0 && (
                  <section>
                    <p className="text-[11px] font-bold uppercase tracking-widest opacity-60 mb-1">✨ Att komma ihåg</p>
                    <ul className="text-sm leading-relaxed space-y-1">
                      {lesson.lesson.key_points.map((p, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="font-bold text-pink-500">•</span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {lesson.lesson.story && (
                  <section className="rounded-xl border-2 border-black p-3 bg-[#FFFAF0] dark:bg-[#1f1f3a]">
                    <p className="text-[11px] font-bold uppercase tracking-widest opacity-60 mb-1">🎬 Ett ögonblick</p>
                    <p className="text-sm leading-relaxed italic">{lesson.lesson.story}</p>
                  </section>
                )}
                {lesson.lesson.why_it_matters && (
                  <section>
                    <p className="text-[11px] font-bold uppercase tracking-widest opacity-60 mb-1">💡 Varför det spelar roll</p>
                    <p className="text-sm leading-relaxed">{lesson.lesson.why_it_matters}</p>
                  </section>
                )}
                {lesson.lesson.fun_fact && (
                  <section className="rounded-xl border-2 border-black p-3 bg-[#FFC8DD] dark:bg-[#3a1a3a]">
                    <p className="text-[11px] font-bold uppercase tracking-widest opacity-70 mb-1">🌟 Visste du?</p>
                    <p className="text-sm leading-relaxed font-semibold">{lesson.lesson.fun_fact}</p>
                  </section>
                )}
              </div>
            )}

            <button onClick={() => setStep('quiz')}
              className="neo-btn w-full py-3 bg-[#A2D2FF] font-bold text-base flex items-center justify-center gap-2"
              data-testid="learn-start-quiz-btn">
              Kör quiz! <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {step === 'quiz' && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold font-['Fredoka']">Snabb-quiz</h2>
              <span className="text-xs font-bold bg-[#FDF0D5] px-2 py-1 rounded-full border-2 border-black">
                {Object.keys(answers).length}/{lesson.questions.length}
              </span>
            </div>
            <div className="space-y-4">
              {lesson.questions.map((q, qi) => (
                <div key={qi} className="neo-card p-4 bg-[#FFFAF0] dark:bg-[#1f1f3a]" data-testid={`learn-q-${qi}`}>
                  <p className="font-bold mb-3 text-sm">{qi + 1}. {q.question}</p>
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => (
                      <button key={oi}
                        onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                        className={`w-full text-left px-3 py-2 rounded-xl border-2 border-black text-sm transition ${
                          answers[qi] === oi ? 'bg-[#A2D2FF] font-bold' : 'bg-white dark:bg-[#2d2d54] hover:bg-[#FDF0D5]'
                        }`}
                        data-testid={`learn-q-${qi}-opt-${oi}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={submit} disabled={!allAnswered || submitting}
              className="neo-btn w-full py-3 mt-5 bg-[#B9FBC0] font-bold text-base disabled:opacity-50"
              data-testid="learn-submit-btn">
              {submitting ? 'Skickar…' : 'Skicka svar'}
            </button>
          </>
        )}

        {step === 'result' && result && (
          <>
            <div className="text-center mb-4">
              <div className="text-6xl mb-2">
                {result.score === 100 ? '🏆' : result.score >= 66 ? '⭐' : '📚'}
              </div>
              <h2 className="text-3xl font-bold font-['Fredoka']" data-testid="learn-result-score">{result.score}%</h2>
              <p className="text-sm opacity-75">{result.correct_count} / {result.total} rätt · +{result.points_earned} poäng · 🔥 {result.streak} dagars streak</p>
            </div>
            <div className="space-y-2">
              {result.review.map((q, i) => (
                <div key={i} className="text-sm p-3 rounded-xl border-2 border-black bg-[#FDF0D5]">
                  <p className="font-semibold mb-1">{i + 1}. {q.question}</p>
                  <p className="text-xs">✅ {q.options[q.correct]}</p>
                </div>
              ))}
            </div>
            <button onClick={() => { onComplete(); onClose(); }}
              className="neo-btn w-full py-3 mt-5 bg-[#FFC8DD] font-bold text-base"
              data-testid="learn-close-btn">
              Klart! Vi ses imorgon 🌸
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function LearnHub({ onUpdate }) {
  const [category, setCategory] = useState(() => localStorage.getItem('learn_category') || 'history');
  const [language, setLanguage] = useState(() => localStorage.getItem('learn_language') || 'en');
  const [langMode, setLangMode] = useState(() => localStorage.getItem('learn_lang_mode') || 'lesson');
  const [languages, setLanguages] = useState([]);
  const [pathData, setPathData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todayLesson, setTodayLesson] = useState(null);
  const [loadingLesson, setLoadingLesson] = useState(false);

  const langParam = useMemo(() => category === 'language' ? language : null, [category, language]);

  useEffect(() => {
    localStorage.setItem('learn_category', category);
    if (category === 'language') localStorage.setItem('learn_language', language);
    localStorage.setItem('learn_lang_mode', langMode);
  }, [category, language, langMode]);

  useEffect(() => {
    axios.get(`${API}/learn/languages`, { withCredentials: true })
      .then((r) => setLanguages(r.data.languages || []))
      .catch(() => {});
  }, []);

  const fetchPath = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days: 14, category };
      if (langParam) params.language = langParam;
      const { data } = await axios.get(`${API}/learn/path`, { params, withCredentials: true });
      setPathData(data);
    } catch (e) {
      toast.error('Kunde inte ladda lärväg');
    } finally {
      setLoading(false);
    }
  }, [category, langParam]);

  useEffect(() => { fetchPath(); }, [fetchPath]);

  const openToday = async () => {
    setLoadingLesson(true);
    try {
      const params = { category };
      if (langParam) params.language = langParam;
      const { data } = await axios.get(`${API}/learn/today`, { params, withCredentials: true });
      setTodayLesson(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte ladda lektion');
    } finally {
      setLoadingLesson(false);
    }
  };

  const handleNodeOpen = (node) => {
    if (node.is_today && !node.completed) openToday();
    else if (node.completed) toast.info('Redan klar! Kom tillbaka imorgon för en ny 🌸');
  };

  const onCompleteLesson = async () => {
    await fetchPath();
    if (onUpdate) await onUpdate();
  };

  const activeCat = CATEGORIES.find((c) => c.id === category) || CATEGORIES[0];
  const activeLang = languages.find((l) => l.code === language);
  const headerLabel = category === 'language' && activeLang
    ? `${activeLang.flag} ${activeLang.name_sv}`
    : `${activeCat.emoji} ${activeCat.label}`;
  const subtitle = category === 'history'
    ? 'En bit världshistoria om dagen 🏺'
    : category === 'trivia'
    ? 'Slumpad allmänkunskap som faktiskt fastnar 🎲'
    : `Lär dig ${activeLang ? activeLang.name_sv : '...'} ett ord i taget ✨`;

  return (
    <div className="space-y-5" data-testid="learn-hub">
      {/* Category tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="learn-category-tabs">
        {CATEGORIES.map((c) => (
          <button key={c.id} onClick={() => setCategory(c.id)}
            className={`neo-btn px-4 py-2 text-sm font-bold flex items-center gap-1.5 transition ${
              category === c.id
                ? `bg-gradient-to-r ${c.accent} text-white scale-[1.04]`
                : 'bg-white opacity-80 hover:opacity-100'
            }`}
            data-testid={`learn-cat-${c.id}`}>
            <span className="text-base">{c.emoji}</span> {c.label}
          </button>
        ))}
      </div>

      {/* Language picker (only when språk) */}
      {category === 'language' && languages.length > 0 && (
        <>
          <div className="flex gap-1.5 flex-wrap" data-testid="learn-language-tabs">
            {languages.map((l) => (
              <button key={l.code} onClick={() => setLanguage(l.code)}
                className={`px-3 py-1.5 rounded-full border-2 border-black text-xs font-bold transition ${
                  language === l.code ? 'bg-[#BBF7D0] scale-105' : 'bg-white opacity-80 hover:opacity-100'
                }`}
                data-testid={`learn-lang-${l.code}`}>
                <span className="mr-1">{l.flag}</span>{l.name_sv}
              </button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="learn-lang-mode-tabs">
            <button onClick={() => setLangMode('lesson')}
              className={`neo-btn px-4 py-1.5 text-xs font-bold flex items-center gap-1.5 ${
                langMode === 'lesson' ? 'bg-[#A2D2FF]' : 'bg-white opacity-80'
              }`}
              data-testid="learn-mode-lesson">
              <BookOpen className="w-3.5 h-3.5" /> Dagens lektion
            </button>
            <button onClick={() => setLangMode('chat')}
              className={`neo-btn px-4 py-1.5 text-xs font-bold flex items-center gap-1.5 ${
                langMode === 'chat' ? 'bg-[#FFE8D6]' : 'bg-white opacity-80'
              }`}
              data-testid="learn-mode-chat">
              <MessageCircle className="w-3.5 h-3.5" /> Chatta med hunden 🐶
            </button>
          </div>
        </>
      )}

      {/* Chat mode shortcut */}
      {category === 'language' && langMode === 'chat' && activeLang && (
        <PetLangChat language={language} languageLabel={activeLang.name_sv} />
      )}

      {category === 'language' && langMode === 'chat' ? null : loading ? (
        <div className="neo-card p-8 text-center animate-pulse-soft">Laddar din lärväg...</div>
      ) : !pathData ? null : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-2xl font-bold font-['Fredoka'] flex items-center gap-2">
                <BookOpen className="w-6 h-6" /> {headerLabel}
              </h2>
              <p className="text-xs opacity-75 mt-1">{subtitle}</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 bg-[#FF6B6B] text-white px-3 py-2 rounded-full border-2 border-black font-bold">
                <Flame className="w-4 h-4" fill="currentColor" />
                <span data-testid="learn-streak">{pathData.streak}-day streak</span>
              </div>
              <button onClick={fetchPath} className="neo-btn p-2 bg-white dark:bg-gray-800"
                data-testid="learn-refresh-btn" title="Uppdatera">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Today CTA */}
          {(() => {
            const today = pathData.path[pathData.path.length - 1];
            if (today && today.is_today && !today.completed) {
              return (
                <div className="neo-card p-5 relative overflow-hidden"
                  style={{ background: `linear-gradient(135deg, #FFC8DD 0%, #A2D2FF 100%)` }}
                  data-testid="learn-today-cta">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-10 h-10 text-pink-700" />
                    <div className="flex-1">
                      <p className="text-xs font-bold uppercase tracking-wider">Idag</p>
                      <h3 className="text-xl font-bold font-['Fredoka']">
                        {today.topic || 'En ny lektion väntar!'}
                      </h3>
                      <p className="text-xs opacity-80 italic">{today.era || 'Tryck för att börja'}</p>
                    </div>
                    <button onClick={openToday} disabled={loadingLesson}
                      className="neo-btn px-5 py-3 bg-white text-black font-bold disabled:opacity-50"
                      data-testid="learn-start-today-btn">
                      {loadingLesson ? 'Laddar…' : 'Starta →'}
                    </button>
                  </div>
                </div>
              );
            }
            if (today && today.completed) {
              return (
                <div className="neo-card p-4 bg-[#B9FBC0]" data-testid="learn-today-done">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-7 h-7" />
                    <p className="text-sm font-bold font-['Fredoka']">Dagens lektion klar! Poäng: {today.score}% ⭐</p>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* Path */}
          <div className="neo-card p-6 bg-[#FFFAF0] dark:bg-[#1f1f3a]">
            <p className="text-xs font-bold uppercase tracking-wider opacity-70 mb-4">Din resa</p>
            <div className="flex flex-col gap-4">
              {pathData.path.map((node, i) => (
                <PathNode key={`${category}-${langParam || ''}-${node.date}`}
                  node={node} index={i} category={category} onOpen={handleNodeOpen} />
              ))}
            </div>
          </div>
        </>
      )}

      {todayLesson && (
        <LessonModal lesson={todayLesson} category={category} language={langParam}
          onClose={() => setTodayLesson(null)} onComplete={onCompleteLesson} />
      )}
    </div>
  );
}
